import {
  Typography,
  Grid,
  Divider,
  Card,
  CardActions,
  CardContent,
  Collapse,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { bloqueioTipoDescriptions } from 'common/genericDescriptions';
import { BloqueioDocumentResponse, BloqueioDocumentType, BloqueioResponse } from 'common/genericTypes';
import { MdSpace, SmSpace, Button, TextButton } from 'm2m.components';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { formatDate } from 'utils/date';
import Timeline from '@material-ui/lab/Timeline';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import {
  Cancel,
  CheckCircle,
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
  GetApp,
  InsertDriveFile,
} from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import { RootState } from 'store';

const useStyles = makeStyles(() =>
  createStyles({
    timelineItem: {
      '&:before': {
        display: 'none',
      },
    },
    timelineContent: {
      padding: '0px 16px',
    },
  })
);

enum Acao {
  Bloqueio = 'BLOQUEIO',
  Liberacao = 'LIBERACAO',
}

interface Historico {
  data: Date;
  justificativa?: string;
  acao: Acao;
  anexos?: Array<BloqueioDocumentResponse>;
}

interface HistoricoGroup {
  [key: string]: Array<Historico>;
}

const acaoIcons: { [key: string]: JSX.Element } = {
  [Acao.Bloqueio]: <Cancel />,
  [Acao.Liberacao]: <CheckCircle />,
};

const acaoDescriptions: { [key: string]: string } = {
  [Acao.Bloqueio]: 'Bloqueio',
  [Acao.Liberacao]: 'Liberação',
};

const mapStateToProps = (state: RootState) => ({
  investidorApi: state.investidorApi,
});

interface Props {
  bloqueio: BloqueioResponse;
}

const HistoricoBloqueio: React.FC<Props & ReturnType<typeof mapStateToProps>> = (props) => {
  const {
    bloqueio,
    investidorApi: { getBloqueioDocumentDownloadLink },
  } = props;
  const [historicos, setHistoricos] = useState<Array<Historico>>([]);
  const [groupedHistoricos, setGroupedHistoricos] = useState<HistoricoGroup>();
  const [expandedHistoricoIndex, setExpandedHistoricoIndex] = useState<number>();
  const dispatch = useDispatch();
  const classes = useStyles();
  const genericClasses = useGenericStyles();

  const handleToggleMoreDetails = (historicoIndex: number) => {
    setExpandedHistoricoIndex(expandedHistoricoIndex === historicoIndex ? undefined : historicoIndex);
  };

  const handleDownloadBloqueioDocument = async (documentId: string, name: string) => {
    try {
      const url = await getBloqueioDocumentDownloadLink(dispatch, bloqueio.id, documentId);
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (!bloqueio) {
      setHistoricos([]);
      return;
    }

    let hist: Array<Historico> = [];
    hist.push({
      data: new Date(bloqueio.dataDeCriacao),
      justificativa: bloqueio.justificativa,
      acao: Acao.Bloqueio,
      anexos: bloqueio.documentos?.filter((_) => _.tipo === BloqueioDocumentType.Bloqueio),
    });

    if (!!bloqueio.dataDeLiberacao) {
      hist.push({
        data: new Date(bloqueio.dataDeLiberacao),
        justificativa: bloqueio.justificativaLiberacao,
        acao: Acao.Liberacao,
        anexos: bloqueio.documentos?.filter((_) => _.tipo === BloqueioDocumentType.Liberado),
      });
    }
    setHistoricos(hist);
  }, [bloqueio, setHistoricos]);

  useEffect(() => {
    const grouped = historicos
      ?.sort((a, b) => +moment(a.data) - +moment(b.data))
      .reduce<HistoricoGroup>((acc, curr) => {
        const date = formatDate(curr.data);
        if (acc[date]) {
          acc[date].push(curr);
        } else {
          acc[date] = [curr];
        }
        return acc;
      }, {});
    setGroupedHistoricos(grouped);
  }, [historicos, setGroupedHistoricos]);

  return (
    <Timeline>
      {!!groupedHistoricos &&
        Object.values(groupedHistoricos)?.map((historicos, index) => (
          <React.Fragment key={index}>
            <TimelineItem className={classes.timelineItem}>
              <TimelineSeparator>
                <FiberManualRecord style={{ margin: 2 }} fontSize="small" color="primary" />
                <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
              </TimelineSeparator>
              <TimelineContent className={classes.timelineContent}>
                <Typography variant="body2">{moment(historicos?.[0].data).format('dddd DD/MM/YYYY')}</Typography>
              </TimelineContent>
            </TimelineItem>
            {historicos.map((historico, histIndex) => (
              <TimelineItem className={classes.timelineItem}>
                <TimelineSeparator>
                  <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                  {acaoIcons[historico.acao]}
                  <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                </TimelineSeparator>
                <TimelineContent className={classes.timelineContent}>
                  <Card variant="outlined" style={{ maxWidth: '40%', margin: '10px 0' }}>
                    <CardContent style={{ paddingBottom: 16 }}>
                      <Typography variant="body2" display={'inline'}>
                        {acaoDescriptions[historico.acao]}
                      </Typography>
                      &nbsp;<Typography display={'inline'}>{bloqueioTipoDescriptions[bloqueio.tipo]}</Typography>
                    </CardContent>
                    {(!!historico.justificativa || !!historico.anexos?.length) && (
                      <>
                        <Collapse in={expandedHistoricoIndex === histIndex} timeout="auto" unmountOnExit>
                          <CardContent style={{ paddingTop: 0 }}>
                            <Divider />
                            <SmSpace />
                            <Typography variant="body2">{historico.justificativa}</Typography>
                            <MdSpace />
                            {historico.anexos?.map((document, index) => (
                              <>
                                {!!index && <Divider style={{ margin: '10px 0px' }} />}
                                <Grid key={document.id} container alignItems={'center'} spacing={2}>
                                  <Grid item>
                                    <InsertDriveFile fontSize={'small'} />
                                  </Grid>
                                  <Grid className={genericClasses.wrapTxt} item md>
                                    <Typography variant="caption">{`${document.fileName} - ${
                                      Math.round((document.size / 1000000) * 100) / 100
                                    } MB`}</Typography>
                                  </Grid>
                                  <Grid item>
                                    <Button
                                      startIcon={<GetApp fontSize={'small'} />}
                                      onClick={() => handleDownloadBloqueioDocument(document.id, document.fileName)}
                                      variant="text"
                                    >
                                      {'Download'}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </>
                            ))}
                          </CardContent>
                        </Collapse>
                        <CardActions style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}>
                          <TextButton
                            aria-expanded={expandedHistoricoIndex === histIndex}
                            size="small"
                            onClick={() => handleToggleMoreDetails(histIndex)}
                            endIcon={expandedHistoricoIndex === histIndex ? <ExpandLess /> : <ExpandMore />}
                          >
                            {expandedHistoricoIndex === histIndex ? 'Menos Informações' : 'Mais Informações'}
                          </TextButton>
                        </CardActions>
                      </>
                    )}
                  </Card>
                </TimelineContent>
              </TimelineItem>
            ))}
          </React.Fragment>
        ))}
    </Timeline>
  );
};

export default connect(mapStateToProps)(HistoricoBloqueio);
